var IconButton          = require("app/ui/IconButton"),
    MediaRepository     = require("app/Domain/Ott/Api/V2/DataAccess/Media/Repository"),
    ShareDataRepository = require("app/Domain/OttMetaInjector/Api/V1/DataAccess/ShareData/Repository"),
    ShareFailMessage    = require("./ShareFailMessage"),
    SharePopup          = require("./SharePopup"),
    ShareSuccessMessage = require("./ShareSuccessMessage"),
    browser             = require("bowser").getParser(window.navigator.userAgent),
    buttons             = require("app/theme/buttons"),
    config              = require("app/config"),
    dataUrlToBlob       = require("app/utils/dataUrlToBlob"),
    frameThrottle       = require("enyo-perf/utils").frameThrottle,
    get                 = require("object-path").get,
    icons               = require("app/theme/icons"),
    isMobile,
    kind                = require("enyo/kind"),
    logger              = require("enyo/logger"),
    platformType        = browser.getPlatform().type,
    transformMediaLinks = require("app/Domain/Ott/Api/V2/DataTransformers/transformMediaLinks");

require("./ShareButton.less");

isMobile = platformType === "mobile" || platformType === "tablet";

module.exports = kind(/** @lends  app.Sharing.ShareButton.prototype */ {

    /**
     * @private
     */
    name: "app.Sharing.ShareButton",

    /**
     * @private
     */
    kind: IconButton,

    /**
     * @private
     */
    classes: "app-sharing-share-button",

    /**
     * @private
     */
    canGenerate: get(config, "sharing.enabled", false),

    /**
     * @public
     */
    theme: buttons["app.Sharing.ShareButton"],

    /**
     * @public
     */
    shareIcon: icons["app.Sharing.ShareButton.share"],

    /**
     * @public
     */
    loadingIcon: icons["app.Sharing.ShareButton.loading"],

    /**
     * The mediaItem
     *
     * @type  {?Object}
     * @public
     */
    mediaItem: null,

    /**
     * @public
     */
    showCaption: true,

    /**
     * @public
     */
    showToast: true,

    /**
     * @readonly
     * @public
     */
    isLoading: false,

    /**
     * @readonly
     * @public
     */
    isSharing: false,

    /**
     * @private
     */
    shareData: null,

    /**
     * @private
     */
    observers: {
        update: ["mediaItem", "isSharing", "showCaption"]
    },

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update    = frameThrottle(this.bindSafely(this.update));
            this.shareData = {};
            sup.apply(this, arguments);

            // Update content
            this.observe("app.$.i18nService.translation", this.update);
            this.update();
            this.isLoadingChanged();
        };
    }),

    /**
     * @private
     */
    update: function() {
        var $i18nService = this.app.$.i18nService,
            mediaItem    = this.get("mediaItem") || null,
            showCaption  = this.get("showCaption");

        // No media item
        if (!mediaItem) {
            this.hide();
            this.set("caption", null);
            this.setAttribute(null);
        }

        // Media item present
        else {
            this.set("caption", showCaption ? $i18nService.getString("app.Sharing.ShareButton.share") : null);
            this.setAttribute("title", $i18nService.getString("app.Sharing.ShareButton.share"));
            this.show();
        }

        // Clear share data, we'll lazily generate it as needed
        this.set("shareData", {});
    },

    /**
     * @private
     */
    isLoadingChanged: function() {
        if (this.isLoading) {
            !this.hasClass("loading") && this.addClass("loading");
            this.set("icon", this.loadingIcon);
        }
        else {
            while (this.hasClass("loading")) {
                this.removeClass("loading");
            }
            this.set("icon", this.shareIcon);
        }
    },

    /**
     * @private
     */
    tap: function() {
        var shareUrlPromise,
            shareMetadataPromise,
            shareFilesPromise;

        if (this.isSharing || !this.shareData) {
            return;
        }

        this.set("isSharing", true);
        this.set("isLoading", true);

        shareUrlPromise      = this.resolveShareUrl();
        shareMetadataPromise = this.resolveShareMetadata();

        // No native sharing support
        if (!navigator.share) {
            Promise.all([shareUrlPromise, shareMetadataPromise])
                .then(this.bindSafely(function() {
                    this.fallbackShare();
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    this.onShareFailed();
                }))
                .finally(this.bindSafely(function() {
                    this.set("isLoading", false);
                }));
        }

        // Attempt native sharing
        else {
            shareFilesPromise = this.resolveShareFiles(shareMetadataPromise);
            Promise.all([shareUrlPromise, shareMetadataPromise, shareFilesPromise])
                .then(this.bindSafely(function() {
                    var shareData       = this.get("sharedData"),
                        nativeShareData = {};

                    if (shareData.url) {
                        nativeShareData.url  = shareData.url;
                        nativeShareData.text = shareData.url;
                    }

                    if (shareData.title) {
                        nativeShareData.title = shareData.title;
                    }

                    if (shareData.files) {
                        nativeShareData.files = shareData.files;
                    }

                    if (isMobile
                        && navigator.canShare
                        && navigator.canShare(nativeShareData)) {
                        this.nativeShare(nativeShareData);
                    }
                    else {
                        this.fallbackShare();
                    }
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    if (this.shareData.url) {
                        this.fallbackShare();
                    }
                }))
                .finally(this.bindSafely(function() {
                    this.set("isLoading", false);
                }));
        }
    },

    /**
     * Load the media item and it's ancestry to generate a share link.
     *
     * @returns  {Promise}  Resolves if shareUrl was added to shareData, rejects
     *                      otherwise.
     * @private
     */
    resolveShareUrl: function() {
        var shareData = this.shareData,
            mediaItem = this.get("mediaItem") || null,
            mediaType = get(mediaItem, "type", null),
            mediaSlug = get(mediaItem, "meta.slug", null),
            mediaId   = get(mediaItem, "id", null),
            language  = this.app.$.i18nService.get("language"),
            mediaPromise;

        // Share url already on shareData
        if (shareData.url) {
            return Promise.resolve();
        }

        if (mediaSlug) {
            mediaPromise = MediaRepository.getByTypeAndSlug(
                false,
                language,
                mediaType,
                mediaSlug
            );
        }
        else {
            mediaPromise = MediaRepository.getByTypeAndId(
                false,
                language,
                mediaType,
                mediaId
            );
        }

        // We need to load an expanded media item to resolve the share url.
        return mediaPromise.then(this.bindSafely(function(response) {
            var rootPath = get(config, "rootPath", null),
                media;

            if (response.status === "ok") {
                media = transformMediaLinks(
                    language,
                    response.data,
                    mediaItem.playlistDetails
                );

                if (media.links.share) {
                    shareData.url = [
                        window.location.origin,
                        rootPath || "",
                        media.links.share
                    ].join("");
                    this.set("shareData", shareData);

                    return;
                }
            }

            throw new Error("Cannot load media item for sharing");
        }));
    },

    /**
     * Load the media item and it's ancestry to generate a share link.
     *
     * @returns  {Promise}  Resolves if shareUrl was added to shareData, rejects
     *                      otherwise.
     * @private
     */
    resolveShareMetadata: function() {
        var shareData = this.shareData,
            mediaItem = this.get("mediaItem") || null,
            language  = this.app.$.i18nService.get("language");

        // Share url already on shareData
        if (shareData.url) {
            return Promise.resolve();
        }

        // We need to load an expanded media item to resolve the share url.
        return ShareDataRepository.getById(
            false,
            language,
            get(mediaItem, "id", null)
        )
            .then(this.bindSafely(function(response) {
                var title,
                    description,
                    images;

                if (response.status === "ok") {
                    title       = get(response, "data.title", null);
                    description = get(response, "data.description", null);
                    images      = get(response, "data.images", []);

                    if (title) {
                        shareData.title = title;
                    }

                    if (description) {
                        shareData.description = description;
                    }

                    if (images) {
                        shareData.images = images;
                    }

                    this.set("shareData", shareData);

                    return;
                }

                throw new Error("Cannot load share data for sharing");
            }));
    },

    /**
     * @param    {Promise}  shareMetadataPromise  The promise that resolves the
     *                                            share metadata, which includes
     *                                            the images.
     * @returns  {Promise}  A promise that resolves when the shareData is ready.
     * @public.
     */
    resolveShareFiles: function(shareMetadataPromise) {
        var shareData = this.shareData;

        if (shareData.images) {
            return shareMetadataPromise.then(this.bindSafely(function() {
                var files,
                    i;

                for (i = 0; i < shareData.images.length; i++) {
                    files.push(shareData.images[i].url);
                }

                return this.convertFileUrlsToBlobs(files);
            })).then(this.bindSafely(function(files) {
                shareData.files = files;
                this.set("shareData", shareData);
            }));
        }
        else {
            return Promise.resolve();
        }
    },

    /**
     * @param    {Array<string|File>}  files  The files
     * @returns  {Array}               An array of blobs
     * @private
     */
    convertFileUrlsToBlobs: function(files) {
        // Fetch all files
        return Promise
            .allSettled(this.bindSafely(files.map(function(file) {
                // Already a File blob
                if (file instanceof File) {
                    return Promise.resolve(file);
                }

                // Handle Data URL
                else if ((/^data:/).test(file)) {
                    return Promise.resolve(dataUrlToBlob(file));
                }

                // Fetch single file
                else {
                    return fetch(file)
                        .then(function(response) {
                            // Return File or `null`
                            return response.ok ? response.blob().then(function(blob) {
                                var fileName = file.split("/").pop(),
                                    fileType = blob.type;

                                return new File([blob], fileName, {type: fileType});
                            }) : null;
                        })
                        .catch(function() {
                            // Failed, return `null`
                            return null;
                        });
                }
            })))
            .then(this.bindSafely(function(results) {
                return results
                    .filter(function(result) {
                        // Reject `null` results
                        return result.status === "fulfilled" && result.value !== null;
                    })
                    .map(function(result) {
                        // Return blobs
                        return result.value;
                    });
            }));
    },

    /**
     * Trigger native sharing UI
     *
     * @param  {Object}  nativeShareData  Share data transformed for native
     *                                    sharing
     * @private
     */
    nativeShare: function(nativeShareData) {
        navigator.share(nativeShareData)
            .then(this.bindSafely(function() {
                this.onShareSuccess();
            }))
            .catch(this.bindSafely(function(error) {
                // Share canceled by user
                if (error.name === "AbortError" || error.code === "AbortError") {
                    // No-op
                }

                // Error
                else {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    this.onShareFailed();
                }
            }))
            .finally(this.bindSafely(function() {
                this.set("isSharing", false);
            }));
    },

    /**
     * Show a Popup with hyperlinks as fallback for native sharing API.
     *
     * @private
     */
    fallbackShare: function() {
        var popup;

        // Display Sharing Popup
        popup = this.createComponent({
            name:      "sharePopup",
            kind:      SharePopup,
            shareData: this.shareData
        });
        popup.render();
        popup.requestShow(true, false);
        popup.on("hidden", this.bindSafely(function() {
            popup.destroy();
            this.set("isSharing", false);
        }));
    },

    /**
     * @private
     */
    onShareSuccess: function() {
        var toast;

        if (this.showToast) {
            toast = this.createComponent({
                kind: ShareSuccessMessage
            });

            this.hasNode() && toast.render();
        }
    },

    /**
     * @private
     */
    onShareFailed: function() {
        var message;

        // Display error message
        message = this.createComponent({
            kind: ShareFailMessage
        });
        message.render();
        message.requestShow(true, false);
        message.on("hidden", function() {
            message.destroy();
        });
    }

});
